import { MenuTypes } from '@ori-events/toggle-menu-flyout';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useSearchInput } from './searchInputContext';

export type ActiveMenu = MenuTypes | '';

export interface ActiveMenuContext {
  activeMenu: ActiveMenu;
  hideSearchResultsTab: () => void;
  toggleActiveMenu: (menu: ActiveMenu) => void;
}

const activeMenuContext = createContext<ActiveMenuContext | undefined>(undefined);

const { Provider } = activeMenuContext;

export const ActiveMenuProvider: FC<PropsWithChildren> = ({ children }) => {
  const [activeMenu, setActiveMenu] = useState<ActiveMenu>('');
  const { blur, clearResults, hide, setValue, show } = useSearchInput();
  const toggleActiveMenu = useCallback(
    (menu: ActiveMenu) => {
      if (menu === MenuTypes.SearchInput || menu === MenuTypes.SearchResults) {
        show();
        setActiveMenu(menu);
      } else if (activeMenu === menu) {
        hide();
        setActiveMenu('');
      } else {
        hide();
        setActiveMenu(menu);
      }
    },
    [activeMenu, hide, show],
  );
  const hideSearchResultsTab = useCallback(() => {
    blur();
    clearResults();
    hide();
    setActiveMenu('');
    setValue('');
  }, [blur, clearResults, hide, setValue]);
  const value = useMemo(
    () => ({ activeMenu, hideSearchResultsTab, toggleActiveMenu }),
    [activeMenu, hideSearchResultsTab, toggleActiveMenu],
  );

  return <Provider value={value}>{children}</Provider>;
};

export const useActiveMenu = () => {
  const context = useContext(activeMenuContext);
  if (context) {
    return context;
  }
  throw new Error('useActiveMenuContext must be used within a ActiveMenuProvider.');
};
